import {
  Card,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { Course } from "../types";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

const fetchCourses = async () => {
  const response = await axios.get<Course[]>(ServerRoutes.courses);
  return response.data;
};

const emptyCourse = {
  name: "",
  description: "",
  cover: "",
  duration: "",
  author: "",
  category: "",
};

export function CoursesSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [formValues, setFormValues] = useState(emptyCourse);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["courses"],
    queryFn: fetchCourses,
  });

  const courses = data ?? [];

  const openCreateDialog = () => {
    setFormValues(emptyCourse);
    setIsCreateOpen(true);
  };

  const openEditDialog = (course: Course) => {
    setSelectedCourse(course);
    setFormValues({
      name: course.name ?? "",
      description: course.description ?? "",
      cover: course.cover ?? "",
      duration: course.duration?.toString() ?? "",
      author: course.author ?? "",
      category: course.category ?? "",
    });
    setIsEditOpen(true);
  };

  const openDeleteDialog = (course: Course) => {
    setSelectedCourse(course);
    setIsDeleteOpen(true);
  };

  const closeCreateDialog = () => {
    if (!isSubmitting) {
      setIsCreateOpen(false);
    }
  };

  const closeEditDialog = () => {
    if (!isSubmitting) {
      setIsEditOpen(false);
    }
  };

  const closeDeleteDialog = () => {
    if (!isSubmitting) {
      setIsDeleteOpen(false);
    }
  };

  const handleFormChange = (field: keyof typeof emptyCourse) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCreateCourse = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(ServerRoutes.courses, {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        cover: formValues.cover.trim(),
        duration: Number(formValues.duration) || 0,
        author: formValues.author.trim(),
        category: formValues.category.trim(),
      });
      setSnackbarMessage("Course created successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsCreateOpen(false);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to create course.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCourse = async () => {
    if (!selectedCourse) return;
    setIsSubmitting(true);
    try {
      await axios.patch(`${ServerRoutes.courses}/${selectedCourse.id}`, {
        name: formValues.name.trim(),
        description: formValues.description.trim(),
        cover: formValues.cover.trim(),
        duration: Number(formValues.duration) || 0,
        author: formValues.author.trim(),
        category: formValues.category.trim(),
      });
      setSnackbarMessage("Course updated successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsEditOpen(false);
      setSelectedCourse(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to update course.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCourse = async () => {
    if (!selectedCourse) return;
    setIsSubmitting(true);
    try {
      await axios.delete(`${ServerRoutes.courses}/${selectedCourse.id}`);
      setSnackbarMessage("Course deleted successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setIsDeleteOpen(false);
      setSelectedCourse(null);
      refetch();
    } catch (error) {
      setSnackbarMessage("Failed to delete course.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Card sx={{ p: isMobile ? 2 : 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Courses
        </Typography>
        <Button variant="contained" onClick={openCreateDialog}>
          Add Course
        </Button>
      </Box>

      <Paper elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Failed to load courses.
                </TableCell>
              </TableRow>
            ) : courses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No courses found.
                </TableCell>
              </TableRow>
            ) : (
              courses.map((course) => (
                <TableRow key={course.id}>
                  <TableCell>{course.name}</TableCell>
                  <TableCell>{course.description}</TableCell>
                  <TableCell>{course.category}</TableCell>
                  <TableCell>{course.author}</TableCell>
                  <TableCell>{course.duration}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                      <Tooltip title="View Seasons">
                        <IconButton
                          onClick={() => navigate(`/courses/${course.id}`)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => openEditDialog(course)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => openDeleteDialog(course)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={isCreateOpen} onClose={closeCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Course</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Cover URL"
              value={formValues.cover}
              onChange={handleFormChange("cover")}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Author"
              value={formValues.author}
              onChange={handleFormChange("author")}
              fullWidth
            />
            <TextField
              label="Category"
              value={formValues.category}
              onChange={handleFormChange("category")}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCourse}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEditOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Course</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formValues.name}
              onChange={handleFormChange("name")}
              fullWidth
            />
            <TextField
              label="Description"
              value={formValues.description}
              onChange={handleFormChange("description")}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Cover URL"
              value={formValues.cover}
              onChange={handleFormChange("cover")}
              fullWidth
            />
            <TextField
              label="Duration (minutes)"
              value={formValues.duration}
              onChange={handleFormChange("duration")}
              fullWidth
              type="number"
            />
            <TextField
              label="Author"
              value={formValues.author}
              onChange={handleFormChange("author")}
              fullWidth
            />
            <TextField
              label="Category"
              value={formValues.category}
              onChange={handleFormChange("category")}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditCourse}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isDeleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
        <DialogTitle>Delete Course</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedCourse?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCourse}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
}

